/* eslint-disable react/no-unstable-nested-components */
/**
 * v3.21-1601 · Partner Content Manager 2.0 — subpanels (Dashboard,
 * Required Compliance, Analytics, Folders, Bulk Upload, Inline Viewer).
 * Rendered from PartnerContentManagerPage via a tab switcher.
 */
import { useCallback, useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Loader2, X, Folder, ChevronRight, TrendingUp, ShieldCheck, BarChart3, Users } from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';

const fmtInt = (n) => Number(n || 0).toLocaleString('en-US');

// ────────────────────────── Dashboard Tab ──────────────────────────
export function AdminDashboardTab() {
  const [d, setD] = useState(null);
  useEffect(() => { (async () => { try { const r = await api.get('/content-manager/dashboard'); setD(r.data); } catch { setD(null); } })(); }, []);
  if (!d) return <Card><CardContent className="py-6 flex items-center gap-2 text-slate-500"><Loader2 className="h-4 w-4 animate-spin" />Loading…</CardContent></Card>;
  const t = d.totals || {};
  const Cell = ({ label, value, testId }) => (
    <div className="rounded-2xl bg-white p-3 shadow-sm border-2 border-slate-200" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">{label}</div>
      <div className="text-xl font-extrabold mt-1 text-slate-800">{fmtInt(value)}</div>
    </div>
  );
  return (
    <div className="space-y-4" data-testid="pcm-dashboard-tab">
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <Cell label="Total"       value={t.total_resources}        testId="pcm-d-total" />
        <Cell label="Active"      value={t.active_resources}       testId="pcm-d-active" />
        <Cell label="Featured"    value={t.featured_resources}     testId="pcm-d-featured" />
        <Cell label="Required"    value={t.required_resources}     testId="pcm-d-required" />
        <Cell label="Publish scheduled" value={t.scheduled_publications} testId="pcm-d-sched-pub" />
        <Cell label="Archive scheduled" value={t.scheduled_archives}     testId="pcm-d-sched-arch" />
        <Cell label="Outstanding acks" value={t.outstanding_acks} testId="pcm-d-outstanding" />
        <Cell label="Total downloads"  value={t.total_downloads}  testId="pcm-d-downloads" />
        <Cell label="Total views"      value={t.total_views}      testId="pcm-d-views" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Card><CardHeader className="pb-2"><CardTitle className="text-sm uppercase text-slate-500 tracking-wider">Most viewed</CardTitle></CardHeader>
          <CardContent className="text-xs space-y-1">
            {(d.most_viewed || []).length === 0 && <p className="text-slate-400 italic">No views yet.</p>}
            {(d.most_viewed || []).map((r) => <div key={r.resource?.id} className="flex justify-between"><span className="truncate mr-2">{r.resource?.title}</span><Badge variant="outline" className="text-[10px] py-0 h-4">{r.views}</Badge></div>)}
          </CardContent></Card>
        <Card><CardHeader className="pb-2"><CardTitle className="text-sm uppercase text-slate-500 tracking-wider">Least viewed</CardTitle></CardHeader>
          <CardContent className="text-xs space-y-1">
            {(d.least_viewed || []).map((r) => <div key={r.resource?.id} className="flex justify-between"><span className="truncate mr-2">{r.resource?.title}</span><Badge variant="outline" className="text-[10px] py-0 h-4">{r.views}</Badge></div>)}
          </CardContent></Card>
        <Card><CardHeader className="pb-2"><CardTitle className="text-sm uppercase text-slate-500 tracking-wider">Recently updated</CardTitle></CardHeader>
          <CardContent className="text-xs space-y-1">
            {(d.recently_updated || []).slice(0, 6).map((r) => <div key={r.id} className="flex justify-between"><span className="truncate mr-2">{r.title}</span><span className="text-slate-400">{new Date(r.last_updated).toLocaleDateString()}</span></div>)}
          </CardContent></Card>
      </div>
    </div>
  );
}

// ────────────────────────── Required Compliance ──────────────────────────
export function RequiredComplianceTab() {
  const [rows, setRows] = useState([]);
  const [detail, setDetail] = useState(null);
  useEffect(() => { (async () => { try { const r = await api.get('/content-manager/required-dashboard'); setRows(r.data?.items || []); } catch { setRows([]); } })(); }, []);
  const openDetail = async (r) => {
    try { const rr = await api.get(`/content-manager/required-dashboard/${r.id}`); setDetail(rr.data); }
    catch (e) { toast.error('Failed to load drilldown'); }
  };
  return (
    <div className="space-y-3" data-testid="pcm-required-tab">
      <Card><CardHeader className="pb-2"><CardTitle className="text-sm uppercase text-slate-500 tracking-wider">Required Documents ({rows.length})</CardTitle></CardHeader>
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 uppercase text-[10px] tracking-wider">
              <tr><th className="text-left px-3 py-2">Document</th><th className="text-left px-3 py-2">Version</th><th className="text-left px-3 py-2">Published</th><th className="text-right px-3 py-2">Companies</th><th className="text-right px-3 py-2">Users</th><th className="text-right px-3 py-2">Viewed</th><th className="text-right px-3 py-2">Downloaded</th><th className="text-right px-3 py-2">Ack’d</th><th className="text-right px-3 py-2">Outstanding</th><th className="text-right px-3 py-2">Completion</th></tr>
            </thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={10} className="px-3 py-6 text-center text-slate-400 italic">No required documents.</td></tr>}
              {rows.map((r) => (
                <tr key={r.id} className="border-t hover:bg-slate-50 cursor-pointer" onClick={() => openDetail(r)} data-testid={`pcm-req-row-${r.id}`}>
                  <td className="px-3 py-2 font-medium">{r.title}</td>
                  <td className="px-3 py-2">{r.version || '—'}</td>
                  <td className="px-3 py-2 text-xs">{r.date_published ? new Date(r.date_published).toLocaleDateString() : '—'}</td>
                  <td className="px-3 py-2 text-right">{r.total_companies}</td>
                  <td className="px-3 py-2 text-right">{r.total_users}</td>
                  <td className="px-3 py-2 text-right">{r.viewed}</td>
                  <td className="px-3 py-2 text-right">{r.downloaded}</td>
                  <td className="px-3 py-2 text-right">{r.acknowledged}</td>
                  <td className="px-3 py-2 text-right text-rose-600 font-semibold">{r.outstanding}</td>
                  <td className="px-3 py-2 text-right"><Badge className={`text-white ${r.completion_pct >= 90 ? 'bg-emerald-600' : r.completion_pct >= 50 ? 'bg-amber-500' : 'bg-rose-600'}`}>{r.completion_pct}%</Badge></td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
      {detail && (
        <div className="fixed inset-0 z-[1100] bg-black/40 flex items-center justify-center" onClick={() => setDetail(null)}>
          <div className="bg-white rounded-lg shadow-xl w-[900px] max-w-[95vw] max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="pcm-req-drill">
            <div className="px-5 py-3 border-b flex items-center justify-between">
              <h2 className="text-base font-semibold">{detail.resource.title} — {detail.count} eligible users</h2>
              <button onClick={() => setDetail(null)}><X className="h-5 w-5" /></button>
            </div>
            <table className="w-full text-xs">
              <thead className="bg-slate-50 text-slate-500 uppercase text-[10px] tracking-wider">
                <tr><th className="text-left px-3 py-2">Company</th><th className="text-left px-3 py-2">User</th><th className="text-left px-3 py-2">Delivered</th><th className="text-left px-3 py-2">Viewed</th><th className="text-left px-3 py-2">Downloaded</th><th className="text-left px-3 py-2">Acknowledged</th></tr>
              </thead>
              <tbody>
                {(detail.items || []).map((it) => (
                  <tr key={it.user_id} className="border-t">
                    <td className="px-3 py-1.5">{it.company_name || '—'}</td>
                    <td className="px-3 py-1.5">{it.user_email}</td>
                    <td className="px-3 py-1.5">{it.delivered_at ? new Date(it.delivered_at).toLocaleString() : <span className="text-slate-300">—</span>}</td>
                    <td className="px-3 py-1.5">{it.viewed_at ? new Date(it.viewed_at).toLocaleString() : <span className="text-slate-300">—</span>}</td>
                    <td className="px-3 py-1.5">{it.downloaded_at ? new Date(it.downloaded_at).toLocaleString() : <span className="text-slate-300">—</span>}</td>
                    <td className="px-3 py-1.5">{it.acknowledged_at ? <span className="text-emerald-700 font-semibold">{new Date(it.acknowledged_at).toLocaleString()}</span> : <span className="text-rose-500">Not yet</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ────────────────────────── Analytics ──────────────────────────
export function AnalyticsTab() {
  const [d, setD] = useState(null);
  useEffect(() => { (async () => { try { const r = await api.get('/content-manager/analytics'); setD(r.data); } catch { setD(null); } })(); }, []);
  if (!d) return <Card><CardContent className="py-6 flex items-center gap-2 text-slate-500"><Loader2 className="h-4 w-4 animate-spin" />Loading…</CardContent></Card>;
  const List = ({ title, rows }) => (
    <Card><CardHeader className="pb-2"><CardTitle className="text-sm uppercase text-slate-500 tracking-wider">{title}</CardTitle></CardHeader>
      <CardContent className="text-xs space-y-1">
        {(rows || []).length === 0 && <p className="text-slate-400 italic">No data.</p>}
        {(rows || []).map((r, i) => <div key={i} className="flex justify-between"><span className="truncate mr-2">{r.resource?.title}</span><Badge variant="outline" className="text-[10px] py-0 h-4">{r.count}</Badge></div>)}
      </CardContent></Card>
  );
  return (
    <div className="space-y-4" data-testid="pcm-analytics-tab">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <List title="Most viewed (all-time)"     rows={d.most_viewed_all} />
        <List title="Most viewed (30 days)"      rows={d.most_viewed_30d} />
        <List title="Most downloaded (all-time)" rows={d.most_downloaded_all} />
        <List title="Most downloaded (30 days)"  rows={d.most_downloaded_30d} />
      </div>
      <Card><CardHeader className="pb-2"><CardTitle className="text-sm uppercase text-slate-500 tracking-wider">Average acknowledgement time</CardTitle></CardHeader><CardContent><div className="text-2xl font-bold">{d.avg_ack_hours == null ? '—' : `${d.avg_ack_hours} hours`}</div></CardContent></Card>
      <Card><CardHeader className="pb-2"><CardTitle className="text-sm uppercase text-slate-500 tracking-wider">Completion rate by company</CardTitle></CardHeader>
        <CardContent className="p-0"><table className="w-full text-xs">
          <thead className="bg-slate-50 text-slate-500 uppercase text-[10px]"><tr><th className="text-left px-3 py-2">Company</th><th className="text-right px-3 py-2">Expected</th><th className="text-right px-3 py-2">Done</th><th className="text-right px-3 py-2">%</th></tr></thead>
          <tbody>{(d.completion_by_company || []).map((r) => <tr key={r.company_id} className="border-t"><td className="px-3 py-1.5">{r.company_name}</td><td className="px-3 py-1.5 text-right">{r.expected}</td><td className="px-3 py-1.5 text-right">{r.done}</td><td className="px-3 py-1.5 text-right">{r.completion_pct == null ? '—' : `${r.completion_pct}%`}</td></tr>)}</tbody>
        </table></CardContent>
      </Card>
    </div>
  );
}

// ────────────────────────── Folders ──────────────────────────
export function FoldersTab() {
  const [tree, setTree] = useState(null);
  const [unfoldered, setUnfoldered] = useState(0);
  useEffect(() => { (async () => { try { const r = await api.get('/content-manager/folders/tree'); setTree(r.data.tree || {}); setUnfoldered(r.data.unfoldered_count || 0); } catch { setTree({}); } })(); }, []);
  if (!tree) return <Card><CardContent className="py-6"><Loader2 className="h-4 w-4 animate-spin" /></CardContent></Card>;
  const renderNode = (node, depth = 0) => Object.entries(node).filter(([k]) => !k.startsWith('_')).map(([name, child]) => (
    <div key={name} className="pl-4 border-l" style={{ marginLeft: depth * 12 }}>
      <div className="flex items-center gap-2 py-1 text-sm" data-testid={`pcm-folder-${name.replace(/\s/g,'-')}`}>
        <Folder className="h-3.5 w-3.5 text-emerald-700" /> <span className="font-medium">{name}</span>
        <Badge variant="outline" className="text-[10px] py-0 h-4">{child._count} resource(s)</Badge>
      </div>
      {child._children && renderNode(child._children, depth + 1)}
    </div>
  ));
  return (
    <div className="space-y-3" data-testid="pcm-folders-tab">
      <Card><CardHeader className="pb-2"><CardTitle className="text-sm uppercase text-slate-500 tracking-wider">Folder Tree</CardTitle></CardHeader>
        <CardContent>
          {Object.keys(tree).length === 0 && <p className="text-slate-400 italic text-sm">No folders yet — set a resource’s <code>folder_path</code> to organise.</p>}
          {renderNode(tree)}
          {unfoldered > 0 && <p className="text-xs text-slate-500 mt-3">Unfoldered resources: <strong>{unfoldered}</strong></p>}
        </CardContent>
      </Card>
    </div>
  );
}

// ────────────────────────── Bulk Upload / Bulk Create ──────────────────────────
export function BulkCreateModal({ enums, onClose, onCreated }) {
  const [form, setForm] = useState({ type: 'finance_documents', category: '', tags: '', visibility_kind: 'all_partners', is_featured: false, is_required: false, folder_path: '', publish_at: '', archive_at: '' });
  const [items, setItems] = useState([{ title: '', description: '' }]);
  const [saving, setSaving] = useState(false);
  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async () => {
    const clean = items.filter((it) => (it.title || '').trim());
    if (!clean.length) return toast.error('Add at least one title');
    setSaving(true);
    try {
      const r = await api.post('/content-manager/resources/bulk', {
        type: form.type, category: form.category || null,
        tags: form.tags.split(',').map((s) => s.trim()).filter(Boolean),
        visibility_kind: form.visibility_kind,
        is_featured: form.is_featured, is_required: form.is_required,
        folder_path: form.folder_path || null,
        publish_at: form.publish_at || null, archive_at: form.archive_at || null,
        items: clean,
      });
      toast.success(`Created ${r.data.count} resource(s)`);
      onCreated && onCreated();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Bulk create failed'); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-[1100] bg-black/40 flex items-center justify-center" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl w-[720px] max-w-[95vw] max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="pcm-bulk-modal">
        <div className="px-5 py-3 border-b flex items-center justify-between">
          <h2 className="text-base font-semibold">Bulk create resources</h2>
          <button onClick={onClose}><X className="h-5 w-5" /></button>
        </div>
        <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-3">
          <div><Label className="text-xs">Type</Label>
            <select value={form.type} onChange={(e) => setF('type', e.target.value)} className="w-full h-9 border rounded-md text-sm px-2 mt-1" data-testid="pcm-bulk-type">
              {(enums.resource_types || []).map((t) => <option key={t} value={t}>{t.replaceAll('_',' ')}</option>)}
            </select>
          </div>
          <div><Label className="text-xs">Category</Label><Input value={form.category} onChange={(e) => setF('category', e.target.value)} /></div>
          <div className="md:col-span-2"><Label className="text-xs">Folder path (e.g. Client Documents/Finance)</Label><Input value={form.folder_path} onChange={(e) => setF('folder_path', e.target.value)} data-testid="pcm-bulk-folder" /></div>
          <div><Label className="text-xs">Tags (comma sep)</Label><Input value={form.tags} onChange={(e) => setF('tags', e.target.value)} /></div>
          <div><Label className="text-xs">Visibility</Label>
            <select value={form.visibility_kind} onChange={(e) => setF('visibility_kind', e.target.value)} className="w-full h-9 border rounded-md text-sm px-2 mt-1">
              {(enums.visibility_kinds || []).map((k) => <option key={k} value={k}>{k.replaceAll('_',' ')}</option>)}
            </select>
          </div>
          <div><Label className="text-xs">Publish at</Label><Input type="datetime-local" value={form.publish_at} onChange={(e) => setF('publish_at', e.target.value ? new Date(e.target.value).toISOString() : '')} data-testid="pcm-bulk-publish-at" /></div>
          <div><Label className="text-xs">Archive at</Label><Input type="datetime-local" value={form.archive_at} onChange={(e) => setF('archive_at', e.target.value ? new Date(e.target.value).toISOString() : '')} /></div>
          <div className="flex items-end gap-4">
            <label className="text-sm flex items-center gap-1"><input type="checkbox" checked={form.is_featured} onChange={(e) => setF('is_featured', e.target.checked)} /> Featured</label>
            <label className="text-sm flex items-center gap-1"><input type="checkbox" checked={form.is_required} onChange={(e) => setF('is_required', e.target.checked)} /> Required</label>
          </div>
          <div className="md:col-span-2">
            <Label className="text-xs">Items ({items.length})</Label>
            <div className="space-y-2 mt-1">
              {items.map((it, i) => (
                <div key={i} className="flex gap-2">
                  <Input placeholder="Title" value={it.title} onChange={(e) => { const n = [...items]; n[i].title = e.target.value; setItems(n); }} data-testid={`pcm-bulk-item-title-${i}`} />
                  <Input placeholder="Description" value={it.description} onChange={(e) => { const n = [...items]; n[i].description = e.target.value; setItems(n); }} />
                  <Button size="sm" variant="outline" onClick={() => setItems(items.filter((_, j) => j !== i))}>×</Button>
                </div>
              ))}
              <Button size="sm" variant="outline" onClick={() => setItems([...items, { title: '', description: '' }])} data-testid="pcm-bulk-add-row">+ Add row</Button>
            </div>
          </div>
        </div>
        <div className="px-5 py-3 border-t flex justify-end gap-2 bg-slate-50">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-emerald-700 hover:bg-emerald-800" data-testid="pcm-bulk-submit">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Create all'}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ────────────────────────── Inline Viewer (PDF / image / video) ──────────────────────────
export function InlineViewer({ resource, onClose }) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [contentType, setContentType] = useState('');
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/content-library/resources/${resource.id}/preview`, { responseType: 'blob' });
        setContentType(r.headers['content-type'] || '');
        setBlobUrl(URL.createObjectURL(r.data));
      } catch { toast.error('Preview failed'); }
    })();
    return () => { if (blobUrl) URL.revokeObjectURL(blobUrl); };

  }, [resource.id]);
  const isPdf   = /pdf/i.test(contentType);
  const isImg   = /^image\//i.test(contentType);
  const isVideo = /^video\//i.test(contentType);
  return (
    <div className="fixed inset-0 z-[1200] bg-black/70 flex items-center justify-center" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl w-[1000px] max-w-[95vw] h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="pcm-viewer">
        <div className="px-5 py-3 border-b flex items-center justify-between">
          <div>
            <h2 className="text-base font-semibold">{resource.title}</h2>
            <p className="text-[11px] text-slate-500">{contentType || 'loading…'}</p>
          </div>
          <button onClick={onClose}><X className="h-5 w-5" /></button>
        </div>
        <div className="flex-1 overflow-auto bg-slate-100 flex items-center justify-center p-4">
          {!blobUrl && <Loader2 className="h-6 w-6 animate-spin text-slate-500" />}
          {blobUrl && isPdf   && <iframe title="pdf" src={blobUrl} className="w-full h-full border" data-testid="pcm-viewer-pdf" />}
          {blobUrl && isImg   && <img alt="preview" src={blobUrl} className="max-w-full max-h-full" data-testid="pcm-viewer-img" />}
          {blobUrl && isVideo && <video controls src={blobUrl} className="max-w-full max-h-full" data-testid="pcm-viewer-video" />}
          {blobUrl && !isPdf && !isImg && !isVideo && (
            <div className="text-center space-y-3">
              <p className="text-sm text-slate-600">This file type does not preview in-browser.</p>
              <a href={blobUrl} download={resource.current_version?.original_filename || resource.title} className="inline-block px-4 py-2 bg-emerald-700 text-white rounded" data-testid="pcm-viewer-download">Download to view</a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
